import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import GuardianConsentSection from "@/components/GuardianConsentSection";
import { emptyGuardian, guardianPayload, resolveGuardianSection, validateGuardian } from "@/lib/guardian";
import { X, Check, AlertCircle, Calendar } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

// Labels that indicate a "date signed" field (should be auto-filled and hidden from patient)
const DATE_SIGNED_LABELS = ["date signed", "signature date", "today's date", "signing date", "date of signature"];

/**
 * DeviceFormMode - Patient-facing full-screen form completion mode
 * 
 * This component provides a clean, secure interface for patients to complete
 * forms on clinic devices (tablets, desktops). It hides all staff/admin controls
 * and shows only the form being completed.
 */
export default function DeviceFormMode({
  template,
  patientId,
  appointmentId,
  patientName,
  clinicName,
  clinicLogo,
  onComplete,
  onCancel,
  existingResponses = {}, // For continuing incomplete forms
  submitForm, // optional override: async (payload) => {} (used by the public patient link)
  minorInfo, // { is_minor, guardian_required, patient_age, dob_available } from the backend
  guardianSection: guardianSectionProp, // resolved section (public link passes this straight through)
  successNote = "Returning to reception...",
  autoCloseOnSuccess = true,
}) {
  const [responses, setResponses] = useState(existingResponses);
  const [signatureData, setSignatureData] = useState(null);
  const [guardian, setGuardian] = useState(emptyGuardian());
  const [guardianSignature, setGuardianSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const signatureRef = useRef(null);
  const guardianSignatureRef = useRef(null);
  const formRef = useRef(null);

  // Patient-facing mode: hide the clinic shell while this form is open
  useEffect(() => {
    document.body.classList.add("lf-device-form-open");
    return () => document.body.classList.remove("lf-device-form-open");
  }, []);

  const guardianSection = resolveGuardianSection(template, minorInfo, guardianSectionProp);
  const guardianRequired = Boolean(guardianSection.show && guardianSection.required);

  // Check if form has signature field
  const signatureField = template?.fields?.find(f => f.field_type === "signature");
  // The patient's own signature is optional when a guardian is signing on their behalf
  const signatureRequired = signatureField?.required && !guardianRequired;

  // Check if a field is a "date signed" field (should be auto-filled and hidden from patient)
  const isDateSignedField = (f) => {
    if (f.field_type !== "date") return false;
    const labelLower = (f.label || "").toLowerCase().trim();
    return DATE_SIGNED_LABELS.some(dl => labelLower.includes(dl));
  };

  // Auto-fill date fields that are specifically "date signed" fields (not date of birth, etc.)
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    
    const autoFilledResponses = { ...responses };
    let updated = false;
    
    for (const f of template?.fields || []) {
      if (isDateSignedField(f) && !responses[f.id]) {
        autoFilledResponses[f.id] = today;
        updated = true;
      }
    }
    
    if (updated) {
      setResponses(autoFilledResponses);
    }
  }, [template?.id]); // Only run once when template changes, not on every render

  // Prevent back button and accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (Object.keys(responses).length > 0 && !showSuccess) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [responses, showSuccess]);

  const validateForm = () => {
    const today = new Date().toISOString().split("T")[0];
    
    // Auto-fill any remaining empty date signed fields before validation
    const updatedResponses = { ...responses };
    let responsesUpdated = false;
    
    for (const f of template?.fields || []) {
      if (isDateSignedField(f) && f.required && !updatedResponses[f.id]) {
        updatedResponses[f.id] = today;
        responsesUpdated = true;
      }
    }
    
    if (responsesUpdated) {
      setResponses(updatedResponses);
    }
    
    // Check required fields (use updatedResponses for validation)
    for (const f of template?.fields || []) {
      if (f.required && f.field_type !== "heading" && f.field_type !== "info_text" && f.field_type !== "signature") {
        const val = updatedResponses[f.id];
        if (val === undefined || val === "" || val === null || (f.field_type === "checkbox" && !val)) {
          setValidationError(`Please complete: ${f.label}`);
          // Scroll to the field
          const fieldEl = document.getElementById(`field-${f.id}`);
          if (fieldEl) {
            fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          return false;
        }
      }
    }
    // Check signature if required
    if (signatureRequired) {
      if (!signatureData || !signatureData.data) {
        setValidationError("Please provide your signature to complete this form.");
        return false;
      }
    }
    // Guardian consent (under-18 patients)
    const latestGuardianSignature = guardianSignatureRef.current?.getSignatureData() || guardianSignature;
    const guardianError = validateGuardian(guardian, guardianRequired, latestGuardianSignature);
    if (guardianError) {
      setValidationError(guardianError);
      document.querySelector("[data-testid='guardian-consent-section']")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let finalSignature = signatureData;
      if (signatureRef.current) {
        const sigData = signatureRef.current.getSignatureData();
        if (sigData) {
          finalSignature = sigData;
        }
      }

      const payload = {
        template_id: template.id,
        patient_id: patientId,
        appointment_id: appointmentId,
        responses,
        status: "completed",
        signature_type: finalSignature?.type || null,
        signature_data: finalSignature?.type === "drawn" ? finalSignature.data : null,
        typed_signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        completed_on_device: true,
        guardian: guardianSection.show
          ? guardianPayload(guardian, guardianSignatureRef.current?.getSignatureData() || guardianSignature)
          : null,
      };

      await (submitForm ? submitForm(payload) : api.post("/forms/submissions", payload));
      setShowSuccess(true);
      
      // Auto-close after 2 seconds
      if (autoCloseOnSuccess) {
        setTimeout(() => {
          onComplete?.();
        }, 2000);
      } else {
        onComplete?.();
      }
    } catch (err) {
      setValidationError("There was a problem submitting the form. Please try again.");
    }
    setSubmitting(false);
  };

  const handleSignatureChange = (data) => {
    setSignatureData(data.isEmpty ? null : data);
    if (validationError && data.data) {
      setValidationError("");
    }
  };

  // Success state
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-[10000] bg-[#FAF9F6] flex flex-col items-center justify-center p-6" data-testid="form-submitted-state">
        <div className="w-20 h-20 rounded-full bg-[#205B5A]/10 flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-[#205B5A]" strokeWidth={2} />
        </div>
        <h1 className="text-2xl font-medium text-[#1A1B1A] mb-2" style={H}>
          Form submitted
        </h1>
        <p className="text-sm text-[#757675] text-center">
          Thank you for completing the form, {patientName?.split(" ")[0] || "Patient"}.
        </p>
        <p className="text-xs text-[#757675] mt-4">
          {successNote}
        </p>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[10000] bg-[#FAF9F6] flex flex-col"
      data-testid="device-form-mode"
    >
      {/* Header - Clean patient-friendly branding */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {clinicLogo ? (
            <img src={clinicLogo} alt="" className="h-8 w-auto" />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-[#205B5A]/10 flex items-center justify-center">
              <span className="text-sm font-bold text-[#205B5A]">{clinicName?.[0] || "C"}</span>
            </div>
          )}
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-[#1A1B1A]">{clinicName || "Clinic"}</p>
            <p className="text-xs text-[#757675]">Patient form</p>
          </div>
        </div>
        
        {/* Cancel button - subtle for staff use */}
        {onCancel && (
        <button
          onClick={onCancel}
          className="p-2 text-[#757675] hover:text-[#1A1B1A] hover:bg-[#FAF9F6] rounded-lg transition-colors"
          title="Cancel and return to reception"
          data-testid="device-form-cancel"
        >
          <X className="w-5 h-5" />
        </button>
        )}
      </div>

      {/* Validation error banner */}
      {validationError && (
        <div className="bg-red-50 border-b border-red-100 px-4 sm:px-6 py-3 flex items-center gap-2" data-testid="device-form-error">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600">{validationError}</p>
        </div>
      )}

      {/* Form content */}
      <div ref={formRef} className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-48">
          {/* Form title */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-medium text-[#1A1B1A] mb-1" style={H}>
              {template?.name || "Form"}
            </h1>
            {patientName && (
              <p className="text-sm text-[#757675]">
                For {patientName}
              </p>
            )}
            {guardianRequired && (
              <div className="mt-3 rounded-xl bg-[#205B5A]/[0.06] border border-[#205B5A]/20 px-4 py-3" data-testid="minor-patient-notice">
                <p className="text-sm text-[#205B5A] leading-relaxed">{guardianSection.notice}</p>
              </div>
            )}
          </div>

          {/* Form fields */}
          <div className="space-y-5">
            {template?.fields?.map((f, i) => (
              <div key={f.id || i} id={`field-${f.id}`}>
                {f.field_type === "heading" && (
                  <h2 className="text-lg font-medium text-[#1A1B1A] mt-6 mb-2" style={H}>
                    {f.label}
                  </h2>
                )}
                
                {f.field_type === "info_text" && (
                  <div className="bg-[#F3F2EE] rounded-xl px-4 py-3">
                    <p className="text-sm text-[#4A4B4A] leading-relaxed whitespace-pre-wrap">
                      {f.label}
                    </p>
                  </div>
                )}
                
                {f.field_type === "short_text" && (
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
                      {f.label} {f.required && <span className="text-red-400">*</span>}
                    </label>
                    <input
                      type="text"
                      value={responses[f.id] || ""}
                      onChange={e => setResponses({...responses, [f.id]: e.target.value})}
                      placeholder={f.placeholder}
                      className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
                    />
                  </div>
                )}
                
                {f.field_type === "long_text" && (
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
                      {f.label} {f.required && <span className="text-red-400">*</span>}
                    </label>
                    <textarea
                      value={responses[f.id] || ""}
                      onChange={e => setResponses({...responses, [f.id]: e.target.value})}
                      rows={4}
                      placeholder={f.placeholder}
                      className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A] resize-none"
                    />
                  </div>
                )}
                
                {f.field_type === "date" && !isDateSignedField(f) && (
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
                      {f.label} {f.required && <span className="text-red-400">*</span>}
                    </label>
                    <input
                      type="date"
                      value={responses[f.id] || ""}
                      onChange={e => setResponses({...responses, [f.id]: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
                    />
                  </div>
                )}
                
                {f.field_type === "dropdown" && (
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-1.5 block">
                      {f.label} {f.required && <span className="text-red-400">*</span>}
                    </label>
                    <select
                      value={responses[f.id] || ""}
                      onChange={e => setResponses({...responses, [f.id]: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-[#1A1B1A]/10 text-base focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
                    >
                      <option value="">Select an option...</option>
                      {f.options?.map((o, oi) => (
                        <option key={oi} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {f.field_type === "radio" && (
                  <div>
                    <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">
                      {f.label} {f.required && <span className="text-red-400">*</span>}
                    </label>
                    <div className="space-y-2">
                      {f.options?.map((o, oi) => (
                        <label key={oi} className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-[#FAF9F6]">
                          <input
                            type="radio"
                            name={f.id}
                            value={o}
                            checked={responses[f.id] === o}
                            onChange={e => setResponses({...responses, [f.id]: e.target.value})}
                            className="w-4 h-4 text-[#205B5A] focus:ring-[#205B5A]"
                          />
                          <span className="text-base text-[#4A4B4A]">{o}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {f.field_type === "checkbox" && (
                  <label className="flex items-start gap-3 cursor-pointer p-3 -mx-3 rounded-xl hover:bg-[#FAF9F6] border border-transparent hover:border-[#1A1B1A]/[0.06]">
                    <input
                      type="checkbox"
                      checked={responses[f.id] || false}
                      onChange={e => setResponses({...responses, [f.id]: e.target.checked})}
                      className="w-5 h-5 mt-0.5 rounded border-[#1A1B1A]/20 text-[#205B5A] focus:ring-[#205B5A]"
                    />
                    <span className="text-base text-[#4A4B4A] leading-relaxed">
                      {f.label} {f.required && <span className="text-red-400">*</span>}
                    </span>
                  </label>
                )}
                
                {f.field_type === "signature" && (
                  <div className="mt-4">
                    <SignaturePad
                      ref={signatureRef}
                      label={guardianRequired ? `${f.label} (optional)` : f.label}
                      required={f.required && !guardianRequired}
                      onChange={handleSignatureChange}
                      initialMode="draw"
                    />
                    {guardianRequired && (
                      <p className="mt-2 text-xs text-[#4A4B4A]" data-testid="patient-signature-optional-note">
                        {guardianSection.patient_signature_note}
                      </p>
                    )}
                    {/* Date signed indicator - read-only, recorded automatically at submission using clinic timezone */}
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#757675] bg-[#FAF9F6] rounded-xl px-4 py-3" data-testid="date-signed-indicator">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span>Date signed: <strong className="text-[#1A1B1A]">{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}</strong> <span className="text-[#9A9B9A] text-xs">(recorded automatically)</span></span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <GuardianConsentSection
            section={guardianSection}
            value={guardian}
            onChange={(g) => { setGuardian(g); if (validationError) setValidationError(""); }}
            signatureRef={guardianSignatureRef}
            onSignatureChange={(data) => {
              setGuardianSignature(data.isEmpty ? null : data);
              if (validationError && data.data) setValidationError("");
            }}
          />
        </div>
      </div>

      {/* Fixed bottom submit button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1A1B1A]/[0.06] p-4 sm:p-6">
        <div className="max-w-xl mx-auto">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="device-form-submit"
            className="w-full bg-[#205B5A] text-white py-4 rounded-xl text-base font-semibold hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Submit form
              </>
            )}
          </button>
          <p className="text-[10px] text-[#757675] text-center mt-2.5" data-testid="form-audit-privacy-note">
            Your information is stored securely and used only for the purpose of providing clinical care.
          </p>
        </div>
      </div>
    </div>
  );
}
